// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Vapi implementation of VoiceTransport. Vapi handles WebRTC audio, STT (Deepgram),
// TTS, and turn-taking; our ai-gateway is the BYO-LLM behind the assistant (configured
// server-side, transparent here). The browser holds only an ephemeral webToken + an
// opaque assistantId/conversationId returned by /v1/intake/start — never a private key
// and never any assistant config or PHI.

import type Vapi from '@vapi-ai/web'
import type {
  IntakeMessage,
  MessageRole,
  VoiceCredentials,
  VoiceTransport,
  VoiceTransportHandlers,
} from '../types'
import { formatTime, newMessageId } from '../util'
import { createVoiceLogger } from './voice-log'

/** Narrowed view of the Vapi `message` event payload (SDK types it loosely). */
interface VapiTranscriptMessage {
  type: 'transcript'
  role: MessageRole
  transcriptType: 'partial' | 'final'
  transcript: string
}

function isTranscriptMessage(m: unknown): m is VapiTranscriptMessage {
  if (typeof m !== 'object' || m === null) return false
  const rec = m as Record<string, unknown>
  return (
    rec.type === 'transcript' &&
    typeof rec.transcript === 'string' &&
    (rec.role === 'user' || rec.role === 'assistant' || rec.role === 'system')
  )
}

/** Vapi surfaces the real end reason via a `status-update` message over the data channel. */
interface VapiStatusUpdate {
  type: 'status-update'
  status: string
  endedReason?: string
}

function isStatusUpdate(m: unknown): m is VapiStatusUpdate {
  if (typeof m !== 'object' || m === null) return false
  const rec = m as Record<string, unknown>
  return rec.type === 'status-update' && typeof rec.status === 'string'
}

/** The opaque, non-PHI conversationId the backend keys on lives in the per-call overrides. */
function conversationIdFrom(credentials: VoiceCredentials): string | undefined {
  const vars = (credentials.assistantOverrides as { variableValues?: Record<string, unknown> } | undefined)
    ?.variableValues
  const id = vars?.conversationId
  return typeof id === 'string' ? id : undefined
}

/** Best-effort read of a string field off a loosely-typed SDK event, for logging only. */
function str(obj: unknown, key: string): string | undefined {
  if (typeof obj !== 'object' || obj === null) return undefined
  const v = (obj as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : undefined
}

function num(obj: unknown, key: string): number | undefined {
  if (typeof obj !== 'object' || obj === null) return undefined
  const v = (obj as Record<string, unknown>)[key]
  return typeof v === 'number' ? v : undefined
}

export class VapiVoiceTransport implements VoiceTransport {
  readonly name = 'vapi' as const

  private vapi: Vapi | null = null
  private muted = false
  private log = createVoiceLogger()
  private endedReason: string | undefined
  private startedAt: number | null = null

  isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      typeof RTCPeerConnection !== 'undefined'
    )
  }

  async start(credentials: VoiceCredentials, handlers: VoiceTransportHandlers): Promise<void> {
    this.stop()
    this.endedReason = undefined
    this.startedAt = performance.now()
    this.log = createVoiceLogger({
      conversationId: conversationIdFrom(credentials),
      assistantId: credentials.assistantId,
    })
    this.log.log('call.start', {
      webTokenAcquired: Boolean(credentials.webToken),
      assistantIdPresent: Boolean(credentials.assistantId),
    })

    // Lazy-load the SDK (it pulls in Daily's WebRTC stack) so text-only patients never
    // pay for it — it's fetched only when a call actually starts.
    const { default: VapiCtor } = await import('@vapi-ai/web')
    this.log.log('sdk.loaded')
    const vapi = new VapiCtor(credentials.webToken)
    this.vapi = vapi

    vapi.on('call-start', () => {
      this.log.log('call.connected')
      handlers.onConnected?.()
      handlers.onListening?.()
    })
    vapi.on('call-end', () => {
      this.log.log('call.ended', {
        endedReason: this.endedReason,
        msSinceLastAssistant: this.log.gapSinceLastAssistant(),
        callDurationMs: this.startedAt !== null ? Math.round(performance.now() - this.startedAt) : undefined,
      })
      handlers.onEnded?.({ endedReason: this.endedReason })
    })
    // Vapi emits speech-start when the ASSISTANT begins speaking → report reply latency.
    vapi.on('speech-start', () => {
      this.log.markAssistantSpeechStart()
      handlers.onAssistantSpeaking?.()
    })
    // speech-end marks the assistant done → the patient's turn (start the latency clock).
    vapi.on('speech-end', () => {
      this.log.markPatientSpeechEnd()
      this.log.markAssistantActivity()
      this.log.log('assistant.speech-end')
      handlers.onListening?.()
    })
    vapi.on('volume-level', (level: number) => handlers.onVolume?.(level))
    vapi.on('error', (err: unknown) => {
      const e = err instanceof Error ? err : new Error(String(err))
      // Full error for diagnostics — errors are transport/vendor metadata, never PHI.
      this.log.log('call.error', {
        message: e.message,
        type: str(err, 'type'),
        stage: str(err, 'stage'),
        detail: !(err instanceof Error) ? safeStringify(err) : undefined,
        msSinceLastAssistant: this.log.gapSinceLastAssistant(),
      })
      handlers.onError?.(e)
    })
    vapi.on('message', (message: unknown) => this.handleMessage(message, handlers))

    // ── Connection lifecycle: each stage of joining the Daily room (helps localize a
    // failure to connect vs. a mid-call drop). ────────────────────────────────────
    vapi.on('call-start-progress', (event: unknown) => {
      this.log.log('call.start-progress', {
        stage: str(event, 'stage'),
        status: str(event, 'status'),
        durationMs: num(event, 'duration'),
      })
    })
    vapi.on('call-start-success', (event: unknown) => {
      const callId = str(event, 'callId')
      this.log.setCallId(callId)
      this.log.log('call.start-success', { callId, totalDurationMs: num(event, 'totalDuration') })
    })
    vapi.on('call-start-failed', (event: unknown) => {
      this.log.log('call.start-failed', {
        stage: str(event, 'stage'),
        error: str(event, 'error'),
        totalDurationMs: num(event, 'totalDuration'),
      })
    })

    // ── Transport health: Daily's WebRTC/websocket connection + quality signals. A
    // 'interrupted' network-connection right before a drop is the smoking gun. ──────
    vapi.on('network-connection', (event: unknown) => {
      this.log.log('network.connection', {
        event: str(event, 'event'),
        type: str(event, 'type'),
        msSinceLastAssistant: this.log.gapSinceLastAssistant(),
      })
    })
    vapi.on('network-quality-change', (event: unknown) => {
      this.log.log('network.quality-change', {
        threshold: str(event, 'threshold'),
        quality: num(event, 'quality'),
      })
    })

    // The backend owns the assistant; we join by its id. assistantOverrides carries the
    // per-call variableValues (opaque conversationId, locale) the gateway keys on.
    const call = await vapi.start(
      credentials.assistantId,
      credentials.assistantOverrides as Parameters<Vapi['start']>[1],
    )
    // start() resolves with the Vapi Call once the room is joined — capture its id so every
    // subsequent line (and the backend correlation) shares the same Vapi callId.
    this.log.setCallId(str(call, 'id'))
    this.log.log('call.assistant-connected', { callId: str(call, 'id') })

    this.muted = false
  }

  private handleMessage(message: unknown, handlers: VoiceTransportHandlers): void {
    // Capture the server-reported end reason (data-channel status-update) for the call-end
    // line and onEnded — informational only; we do NOT branch on it.
    if (isStatusUpdate(message)) {
      if (message.endedReason) this.endedReason = message.endedReason
      this.log.log('call.status-update', {
        status: message.status,
        endedReason: message.endedReason,
        msSinceLastAssistant: this.log.gapSinceLastAssistant(),
      })
      return
    }

    if (!isTranscriptMessage(message)) return

    // Log the transcript event WITHOUT its text (PHI) — role, kind, and length only.
    this.log.log('transcript', {
      role: message.role,
      kind: message.transcriptType,
      chars: message.transcript.length,
    })
    // A finalized assistant turn is assistant activity — anchors the inter-turn gap clock.
    if (message.role === 'assistant' && message.transcriptType === 'final') {
      this.log.markAssistantActivity()
    }

    if (message.transcriptType === 'final') {
      const normalized: IntakeMessage = {
        id: newMessageId(),
        role: message.role,
        content: message.transcript,
        modality: 'voice',
        timestamp: formatTime(),
      }
      handlers.onMessage?.(normalized)
    } else {
      handlers.onPartial?.(message.role, message.transcript)
    }
  }

  sendText(text: string): void {
    this.vapi?.send({
      type: 'add-message',
      message: { role: 'user', content: text },
      triggerResponseEnabled: true,
    })
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    this.vapi?.setMuted(muted)
  }

  isMuted(): boolean {
    return this.muted
  }

  stop(): void {
    if (!this.vapi) return
    this.log.log('call.stop-requested', { msSinceLastAssistant: this.log.gapSinceLastAssistant() })
    try {
      this.vapi.removeAllListeners()
      this.vapi.stop()
    } catch {
      // already stopped / not started — ignore
    }
    this.vapi = null
  }
}

/** Compact, PHI-free serialization of a non-Error event object for diagnostics. */
function safeStringify(v: unknown): string | undefined {
  try {
    const s = JSON.stringify(v)
    return s && s.length > 500 ? s.slice(0, 500) : s
  } catch {
    return undefined
  }
}
